/**
 * Google only ever redirects to the one registered callback origin. The backend
 * (`/auth/google/bounce`) verifies the signed OAuth state and returns the tenant
 * origin that started the login, or null. This only decides whether that origin
 * is somewhere else than here, comparing parsed origins so `https://host:443`
 * and `https://host` never bounce to each other forever.
 */
export function bounceTargetFor(
	returnOrigin: string | null | undefined,
	currentOrigin: string,
	search: string,
): string | null {
	if (!returnOrigin) return null;
	let target: string;
	try {
		target = new URL(returnOrigin).origin;
	} catch {
		return null;
	}
	// The backend returns a bare origin; anything the browser parses differently
	// (userinfo, path, odd encoding) is not something we forward a code to.
	if (target !== returnOrigin) return null;
	if (target === new URL(currentOrigin).origin) return null;
	return `${target}/google-oauth-callback${search}`;
}

export const OAUTH_NONCE_KEY = 'oauth_nonce';

/** The nonce inside a signed OAuth state (`<base64 payload>.<signature>`), or null. */
export function oauthStateNonce(state: string | null): string | null {
	if (!state) return null;
	const payload = state.split('.')[0];
	try {
		const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
		const nonce = JSON.parse(json)?.nonce;
		return typeof nonce === 'string' && nonce ? nonce : null;
	} catch {
		return null;
	}
}

/**
 * True when the state's nonce matches the one this tab stored when it started the
 * login. Login start and the final callback POST both happen on the tenant origin
 * in the same tab, so sessionStorage is the right scope.
 */
export function nonceMatchesThisTab(
	state: string | null,
	stored: string | null,
): boolean {
	const nonce = oauthStateNonce(state);
	return nonce !== null && stored !== null && nonce === stored;
}

export const OAUTH_PKCE_VERIFIER_KEY = 'oauth_pkce_verifier';

const base64url = (bytes: Uint8Array): string =>
	btoa(String.fromCharCode(...bytes))
		.replace(/\+/g, '-')
		.replace(/\//g, '_')
		.replace(/=+$/, '');

/** RFC 7636 PKCE pair: a random verifier and its S256 challenge. */
export async function pkcePair(): Promise<{
	verifier: string;
	challenge: string;
}> {
	const verifier = base64url(crypto.getRandomValues(new Uint8Array(32)));
	const digest = await crypto.subtle.digest(
		'SHA-256',
		new TextEncoder().encode(verifier),
	);
	return { verifier, challenge: base64url(new Uint8Array(digest)) };
}
